import { datadogLogs } from '@datadog/browser-logs';
import { useRef } from 'react';

export const performanceMeasureMarkFinished = ({
  labels,
  message = 'performance measurement',
  additionalFacets = {},
  DEBUG = false,
  timeout = 0,
}) => {
  performance.mark(labels.FINISH);

  try {
    performance.measure(labels.MEASURE, labels.START, labels.FINISH);
    performance.clearMarks(labels.START);

    const measures = performance.getEntriesByName(labels.MEASURE);
    performance.clearMeasures(labels.MEASURE);

    const measure = measures[measures.length - 1].toJSON();
    measure.duration = measure.duration - timeout;

    for (const [key, value] of Object.entries(additionalFacets)) {
      measure[key] = value;
    }

    datadogLogs.logger.info(message, { measure });

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log(message, measure);
    }
  } catch (e) {
    if (DEBUG) {
      console.error(e);
    }
  }
};

/**
 * Measure performance and log to datadog (requires starting marker to be set prior to logging)
 * Use this hook in a functional component to specify when to mark the action as completed,
 * but be sure to specify the starting time at some other place in the app (usually in saga
 * or in a onClick method that begins the action)
 * @param {Object}  options
 * @param {Object}  options.labels         The labels to use for performance markers
 * @param {String}  options.labels.START   The start marker
 * @param {String}  options.labels.FINISH  The finish marker
 * @param {String}  options.labels.MEASURE The measure name
 * @param {Object}  additionalFacets       Any other parameters you want to observe in datadog (like componentCount or quoteItemCount)
 * @param {String}  [options.message]      The datadog log message
 * @param {Boolean} [options.DEBUG]        Whether or not to console.log the results
 * @param {Number}  [options.timeout]      The debounce time to wait before logging (useful for capturing multiple renders
 * @param {Boolean} [options.enabled]      Whether or not the measure is active
 * and letting a full render cycle pass to ensure the logged time is meaningful to when the canvas actually paints...this
 * side effect of useTimeout is important, and it works even if the timeout length is 0)
 */
export const usePerformanceMeasure = ({
  labels,
  message = 'performance measurement',
  additionalFacets = {},
  DEBUG = false,
  timeout = 0,
  enabled = true,
}) => {
  const measureTimeout = useRef(null);

  if (!enabled) {
    return;
  }

  const start = performance.getEntriesByName(labels.START);
  if (start.length === 0) {
    return;
  }

  if (measureTimeout.current) {
    clearTimeout(measureTimeout.current);
  }

  measureTimeout.current = setTimeout(() => {
    performanceMeasureMarkFinished({
      labels,
      message,
      additionalFacets,
      DEBUG,
      timeout,
    });
  }, timeout);
};
